<template>
  <div class="roster-root" :class="{ 'light-mode': !isDarkMode }">
    <!-- Roster Header Bar -->
    <div class="roster-bar">
      <div class="d-flex align-center" style="gap: 12px">
        <div class="roster-bar-title">{{ monthName }}</div>
        <div class="roster-bar-stats">
          <span>{{ employees.length }} MA</span>
          <span class="roster-bar-dot">·</span>
          <span>{{ planData.length }} Schichten</span>
        </div>
        <v-chip v-if="moveSource" color="blue accent-2" text-color="white" close @click:close="cancelMove" x-small class="ml-2">
          <v-icon left x-small>mdi-cursor-move</v-icon>
          Ziel wählen: {{ $moment(moveSource.date).format("DD.MM") }}
        </v-chip>
      </div>

      <div class="d-flex align-center" style="gap: 12px">
        <!-- Legend -->
        <div class="roster-legend">
          <div class="roster-legend-item">
            <div class="roster-ldot" style="border: 2px solid #ffd700"></div>
            <span>Spezial</span>
          </div>
          <div class="roster-legend-item">
            <div class="roster-ldot" style="border: 2px dashed #ff5252"></div>
            <span>Einzel</span>
          </div>
          <div class="roster-legend-item">
            <div class="roster-ldot" style="background: #e91e63"></div>
            <span>Schicht</span>
          </div>
        </div>
        <!-- Compact Mode Switch -->
        <v-btn x-small depressed :color="isCompact ? 'success' : 'grey darken-3'" class="roster-quality-btn mr-1" @click="toggleCompact">
          <v-icon left x-small>{{ isCompact ? "mdi-view-compact" : "mdi-view-headline" }}</v-icon>
          Kompakt-Modus
        </v-btn>
        <!-- Quality Score -->
        <v-btn x-small depressed :color="qualityScore.color" class="roster-quality-btn" @click="showQualityPanel = !showQualityPanel">
          <v-icon left x-small>mdi-chart-arc</v-icon>
          {{ qualityScore.score }}%
          <v-icon right x-small class="ml-1">{{ showQualityPanel ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Qualitäts-Score Detail Panel -->
    <v-expand-transition>
      <div v-if="showQualityPanel" class="px-3 pb-3 flex-shrink-0">
        <v-card outlined class="grey darken-4 rounded-lg pa-4">
          <div class="d-flex align-start" style="gap: 24px">
            <!-- Score Gauge -->
            <div class="text-center" style="min-width: 100px">
              <v-progress-circular :value="qualityScore.score" :size="80" :width="8" :color="qualityScore.color" rotate="-90">
                <span class="text-h5 font-weight-bold" :class="qualityScore.color + '--text'">{{ qualityScore.score }}</span>
              </v-progress-circular>
              <div class="text-caption grey--text mt-1">Gesamt</div>
            </div>

            <!-- Kategorien -->
            <div class="flex-grow-1">
              <v-row dense>
                <v-col v-for="cat in qualityScore.categories" :key="cat.name" cols="12" sm="6" md="4" lg="3">
                  <div class="d-flex align-center mb-1">
                    <v-icon x-small :color="cat.color" class="mr-1">{{ cat.icon }}</v-icon>
                    <span class="text-caption font-weight-bold white--text">{{ cat.name }}</span>
                    <v-spacer></v-spacer>
                    <span class="text-caption font-weight-bold" :class="cat.color + '--text'">{{ cat.score }}/{{ cat.maxScore }}</span>
                  </div>
                  <v-progress-linear :value="(cat.score / cat.maxScore) * 100" :color="cat.color" height="4" rounded class="mb-1"></v-progress-linear>
                  <div
                    v-for="(issue, idx) in cat.issues"
                    :key="idx"
                    class="d-flex align-center text-caption"
                    style="font-size: 0.7rem; line-height: 1.3">
                    <v-icon
                      x-small
                      :color="issue.severity === 'error' ? 'red lighten-1' : issue.severity === 'warning' ? 'orange lighten-1' : 'grey lighten-1'"
                      class="mr-1">
                      {{ issue.severity === "error" ? "mdi-alert-circle" : issue.severity === "warning" ? "mdi-alert" : "mdi-information" }}
                    </v-icon>
                    <span class="grey--text text--lighten-1">{{ issue.text }}</span>
                  </div>
                  <div v-if="cat.issues.length === 0" class="text-caption" style="font-size: 0.7rem">
                    <v-icon x-small color="green lighten-1" class="mr-1">mdi-check-circle</v-icon>
                    <span class="green--text text--lighten-1">Perfekt!</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card>
      </div>
    </v-expand-transition>

    <div v-if="loading" class="d-flex justify-center align-center flex-grow-1">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6 grey--text">Lade Plan...</p>
      </div>
    </div>

    <v-card v-else flat outlined dark class="rounded-0 flex-grow-1 d-flex flex-column grey darken-4" style="overflow: hidden">
      <div class="d-flex flex-nowrap grey darken-4 border-bottom" style="z-index: 5">
        <div class="employee-col-header font-weight-bold pa-3 d-flex align-center justify-center grey--text text--lighten-1 text-subtitle-2">
          MITARBEITER / BILANZ
        </div>

        <div class="calendar-grid flex-grow-1 d-flex" :class="{ 'moving-mode': moveSource }">
          <div
            v-for="day in daysInMonth"
            :key="`header-${day.format('YYYY-MM-DD')}`"
            class="day-col text-center py-2 relative-col"
            :class="{
              'weekend-header': day.isoWeekday() >= 6,
              'holiday-header': isPublicHoliday(day),
              'drop-active': moveSource && day.format('YYYY-MM-DD') === moveSource.date,
            }"
            :style="{ width: colWidth + '%' }">
            <div class="text-body-2 font-weight-bold" style="opacity: 0.8">{{ day.format("dd") }}</div>
            <div class="text-h6 font-weight-bold mt-1">{{ day.format("D") }}</div>
            <v-tooltip bottom v-if="isPublicHoliday(day)">
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small color="amber lighten-2" v-bind="attrs" v-on="on" class="mt-n1">mdi-star-four-points</v-icon>
              </template>
              <span>{{ getPublicHolidayName(day) }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <div style="overflow-y: auto; flex-grow: 1" class="custom-scrollbar">
        <div class="d-flex flex-nowrap blue-grey darken-4 border-bottom" v-if="unassignedStats.totalCount > 0 || showUnassignedRow">
          <div class="employee-col-header pa-3 d-flex align-center warning--text">
            <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold">OFFEN ({{ unassignedStats.totalCount }})</span>
          </div>
          <div class="calendar-grid flex-grow-1 d-flex">
            <div
              v-for="day in daysInMonth"
              :key="`unassigned-${day.format('YYYY-MM-DD')}`"
              class="day-col d-flex justify-center align-center py-2"
              :class="{ 'weekend-bg': day.isoWeekday() >= 6 }"
              :style="{ width: colWidth + '%' }">
              <v-menu v-if="getUnassignedShiftsForDay(day).length > 0" offset-y :close-on-content-click="false" max-width="400">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="unassigned-dot elevation-2 pulse-hover">
                    {{ getUnassignedShiftsForDay(day).length }}
                  </div>
                </template>

                <v-card class="grey darken-3 dark">
                  <v-card-title class="subtitle-2 white--text py-2 border-bottom"> Offene Schichten ({{ day.format("DD.MM") }}) </v-card-title>
                  <v-list dense dark class="grey darken-3">
                    <v-list-item v-for="shift in getUnassignedShiftsForDay(day)" :key="shift._id" class="px-2 border-bottom-dashed">
                      <v-list-item-content>
                        <v-list-item-title class="yellow--text font-weight-bold">
                          {{ shift.shiftTemplate ? shift.shiftTemplate.name : "Custom" }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text text--lighten-2">
                          {{ formatShiftTime(shift) }}
                        </v-list-item-subtitle>
                        <div class="text-caption red--text text--lighten-3 mt-1" v-if="shift.allocationNote">
                          {{ shift.allocationNote }}
                        </div>
                      </v-list-item-content>
                      <v-list-item-action class="d-flex flex-row align-center" style="gap: 8px">
                        <v-btn small color="success" depressed @click="openAssignDialog(shift)"> Zuweisen </v-btn>
                        <v-btn icon small color="red lighten-1" @click="deleteShift(shift)" title="Schicht löschen">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>

        <v-expansion-panels
          accordion
          flat
          hover
          v-if="processedEmployees.length > 0"
          multiple
          v-model="openPanels"
          :class="{ 'compact-mode-panels': isCompact }">
          <v-expansion-panel v-for="employee in processedEmployees" :key="employee._id" class="employee-panel border-bottom">
            <v-expansion-panel-header class="pa-0" hide-actions>
              <div class="d-flex flex-nowrap w-100 fill-height">
                <div
                  :class="[
                    'employee-col-info border-right d-flex grey darken-3',
                    isCompact ? 'pa-2 align-center justify-space-between' : 'pa-3 flex-column justify-center',
                  ]"
                  :style="isCompact ? 'height: 40px;' : ''">
                  <template v-if="isCompact">
                    <!-- Compact layout -->
                    <div class="text-truncate mr-2" style="max-width: 155px">
                      <div class="text-body-2 font-weight-bold white--text text-truncate" :title="employee.fullName">
                        {{ employee.fullName }}
                      </div>
                      <div class="text-caption grey--text text-truncate mt-n1" style="font-size: 0.7rem !important" v-if="employee.studioTitle">
                        {{ employee.studioTitle }}
                      </div>
                    </div>
                    <div class="d-flex align-center text-caption" style="gap: 8px; flex-shrink: 0">
                      <div>
                        <span class="grey--text text--lighten-2">S:</span>
                        <span class="white--text font-weight-bold ml-0.5">{{ (employee.sollStunden || 0).toFixed(0) }}</span>
                      </div>
                      <div>
                        <span class="grey--text text--lighten-2">P:</span>
                        <span
                          :class="[
                            'ml-0.5',
                            employee.plannedHours < employee.sollStunden ? 'error--text font-weight-bold' : 'success--text font-weight-bold',
                          ]">
                          {{ (employee.plannedHours || 0).toFixed(0) }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <!-- Original layout -->
                    <div class="d-flex align-center mb-2">
                      <v-avatar size="40" class="mr-3 elevation-2">
                        <img :src="employee.picture ? '/img/profilbilder' + employee.picture : '/img/default-avatar.png'" />
                      </v-avatar>
                      <div class="text-truncate" style="max-width: 190px">
                        <div class="text-subtitle-1 font-weight-bold lh-1 white--text">{{ employee.fullName }}</div>
                        <div class="text-caption grey--text text--lighten-1 mt-1">{{ employee.studioTitle || "Team" }}</div>
                      </div>
                    </div>
                    <div style="font-size: 0.8rem; line-height: 1.4">
                      <div class="d-flex justify-space-between mb-1 px-1">
                        <div>
                          <span class="grey--text text--lighten-1">Soll:</span>
                          <span class="white--text ml-1">{{ (employee.sollStunden || 0).toFixed(0) }}</span>
                        </div>
                        <div>
                          <span class="grey--text text--lighten-1">Plan:</span>
                          <span
                            class="ml-1"
                            :class="employee.plannedHours < employee.sollStunden ? 'error--text font-weight-bold' : 'success--text font-weight-bold'"
                            >{{ (employee.plannedHours || 0).toFixed(0) }}</span
                          >
                        </div>
                      </div>
                      <div class="d-flex justify-space-between align-center mt-1 px-1 grey darken-4 rounded pa-1" style="font-size: 0.75rem">
                        <span class="red--text text--lighten-1 font-weight-bold">F: {{ employee.stats.types.morning }}</span>
                        <span class="blue--text text--lighten-1 font-weight-bold">Z: {{ employee.stats.types.intermediate }}</span>
                        <span class="green--text text--lighten-1 font-weight-bold">S: {{ employee.stats.types.late }}</span>
                      </div>
                      <div class="d-flex justify-space-between align-center mt-2 pt-2 border-top-dashed">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              label
                              :color="Math.abs(employee.stats.diffMonth) > 12 ? 'red darken-3' : 'green darken-3'"
                              v-bind="attrs"
                              v-on="on">
                              {{ employee.stats.diffMonth > 0 ? "+" : "" }}{{ employee.stats.diffMonth.toFixed(0) }}h
                            </v-chip>
                          </template>
                          <span>{{ employee.stats.statusText }}</span>
                        </v-tooltip>
                        <span class="grey--text text--lighten-2 text-caption"
                          ><strong>{{ employee.stats.weekendCount }}</strong> WE</span
                        >
                      </div>
                      <div v-if="getEmployeePrevContext(employee)" class="d-flex align-center mt-1 pt-1" style="font-size: 0.7rem">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip x-small label color="blue-grey darken-3" v-bind="attrs" v-on="on" class="mr-1">
                              <v-icon x-small left>mdi-history</v-icon>
                              {{ getEmployeePrevContext(employee).streak }}d Streak
                            </v-chip>
                          </template>
                          <span>{{ getEmployeePrevContext(employee).streak }} aufeinanderfolgende Arbeitstage am Vormonatsende</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="getEmployeePrevContext(employee).prevWeekends > 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              label
                              :color="getEmployeePrevContext(employee).prevWeekends >= 2 ? 'red darken-4' : 'orange darken-4'"
                              v-bind="attrs"
                              v-on="on">
                              {{ getEmployeePrevContext(employee).prevWeekends }} Vor-WE
                            </v-chip>
                          </template>
                          <span>{{ getEmployeePrevContext(employee).prevWeekends }} Wochenende(n) im Vormonat gearbeitet</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="calendar-grid flex-grow-1 d-flex" :class="{ 'moving-mode': moveSource }">
                  <div
                    v-for="(status, date) in employee.dailyStatus"
                    :key="date"
                    class="day-col d-flex justify-center align-center border-left-light drop-zone cell-hover-trigger"
                    :class="{
                      'weekend-bg': $moment(date).isoWeekday() >= 6,
                      'holiday-bg': isPublicHolidayByStr(date),
                      'drop-active': moveSource && date === moveSource.date,
                    }"
                    :style="{ width: colWidth + '%' }"
                    @click="handleCellClick(employee, date)">
                    <div
                      v-if="status && status.type === 'shift'"
                      style="width: 100%; display: flex; justify-content: center; gap: 4px; flex-wrap: nowrap; padding: 0 2px">
                      <div v-for="shift in status.allShifts" :key="shift._id" style="flex: 1; min-width: 0; display: flex; justify-content: center">
                        <v-menu offset-y :close-on-content-click="true">
                          <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip bottom :disabled="moveSource !== null">
                              <template v-slot:activator="{ on: tooltip }">
                                <div
                                  v-bind="attrs"
                                  v-on="{ ...tooltip, ...menu }"
                                  class="shift-pill elevation-1 d-flex align-center justify-center"
                                  :class="{
                                    'shift-pill-special': isSpecialShift(shift),
                                    'shift-pill-island': shift.isIsland,
                                    'shift-moving': moveSource && moveSource._id === shift._id,
                                  }"
                                  :style="{ backgroundColor: shift.shiftTemplate ? shift.shiftTemplate.color : '#E91E63' }"
                                  @click.stop="handleShiftClick(shift)">
                                  <v-icon x-small color="red accent-2" v-if="shift.isIsland" class="mr-1">mdi-alert-circle</v-icon>
                                  <v-icon x-small color="white" v-if="isSpecialShift(shift)">mdi-star</v-icon>
                                  <span
                                    class="white--text font-weight-bold"
                                    style="font-size: 0.65rem; opacity: 0.95"
                                    v-if="isAdditionalStudioShift(shift)">
                                    {{ getStudioAbbreviation(shift.studio) }}
                                  </span>
                                </div>
                              </template>
                              <div class="text-center">
                                <div class="subtitle-2 font-weight-bold">
                                  {{ shift.shiftTemplate ? shift.shiftTemplate.name : "Spezial" }}
                                </div>
                                <div class="text-caption">{{ formatShiftTime(shift) }}</div>
                                <div class="text-caption font-weight-bold blue-grey--text text--lighten-3" v-if="shift.studio">
                                  Studio: {{ getStudioName(shift.studio) }}
                                </div>
                                <div v-if="shift.allocationNote" class="yellow--text mt-1 text-caption font-weight-bold">
                                  {{ shift.allocationNote }}
                                </div>
                                <div v-if="shift.warning" class="red--text text--lighten-3 font-weight-bold mt-1 text-caption border-top pt-1">
                                  <v-icon x-small color="red lighten-3">mdi-alert</v-icon> {{ shift.warning }}
                                </div>
                              </div>
                            </v-tooltip>
                          </template>
                          <v-list dense>
                            <v-list-item @click="startMove(shift)">
                              <v-icon left small>mdi-cursor-move</v-icon> <v-list-item-title>Verschieben / Tauschen</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="deleteShift(shift)" class="red--text">
                              <v-icon left small color="red">mdi-delete</v-icon> <v-list-item-title>Löschen</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>

                    <v-tooltip bottom v-else-if="status && status.type === 'externalShift'">
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="shift-pill elevation-1 d-flex align-center justify-center"
                          :style="{
                            'background-color': status.data.isVirtualExternal ? '#2E7D32' : '#607D8B',
                            opacity: status.data.isVirtualExternal ? 0.8 : 0.6,
                            cursor: 'default',
                            border: status.data.isVirtualExternal ? '1px dashed #A5D6A7' : 'none',
                          }">
                          <v-icon x-small color="white">
                            {{ status.data.isVirtualExternal ? "mdi-check-circle-outline" : "mdi-home-outline" }}
                          </v-icon>
                        </div>
                      </template>
                      <div class="text-center">
                        <div class="subtitle-2 font-weight-bold" v-if="status.data.isVirtualExternal">
                          Fremdeinsatz bestätigt (Zeiten unbestätigt)
                        </div>
                        <div class="subtitle-2 font-weight-bold" v-else>
                          Anderes Center: {{ status.data.studio ? status.data.studio.name : "Center" }}
                        </div>
                        <div class="text-caption" v-if="!status.data.isVirtualExternal">
                          {{ status.data.shiftTemplate ? status.data.shiftTemplate.name : "Schicht" }}
                          {{ formatShiftTime(status.data) }}
                        </div>
                      </div>
                    </v-tooltip>

                    <v-tooltip bottom v-else-if="status && status.type !== 'shift'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" :color="getStatusColor(status.type)" :style="status.isPending ? 'opacity: 0.65;' : ''">
                          {{ getStatusIcon(status.type) }}
                        </v-icon>
                      </template>
                      <span>
                        {{ getStatusText(status.type) }}{{ status.isPending ? " (Ausstehend)" : "" }}
                        <span v-if="status.note"><br />Info: {{ status.note }}</span>
                      </span>
                    </v-tooltip>

                    <div v-else class="empty-slot fill-height w-100 d-flex justify-center align-center">
                      <v-btn
                        v-if="!moveSource"
                        icon
                        x-small
                        color="grey lighten-1"
                        class="hover-add-btn"
                        @click.stop="openAddShiftDialog(employee, date)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="pa-4 grey darken-4 border-top-dashed mt-4">
          <v-row align="center" justify="center" v-if="availableEmployees.length > 0">
            <v-col cols="12" sm="8" md="6" class="d-flex align-center">
              <v-autocomplete
                v-model="selectedEmployeeToAdd"
                :items="availableEmployees"
                item-text="fullName"
                item-value="_id"
                label="Weiteren Mitarbeiter einblenden..."
                outlined
                dense
                dark
                hide-details
                placeholder="Mitarbeiter suchen"
                class="mr-4">
                <template v-slot:item="{ item }">
                  <v-avatar size="24" class="mr-3">
                    <img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" />
                  </v-avatar>
                  {{ item.fullName }}
                </template>
              </v-autocomplete>
              <v-btn color="primary" depressed :disabled="!selectedEmployeeToAdd" @click="emitAddEmployee">
                <v-icon left>mdi-account-plus</v-icon> Hinzufügen
              </v-btn>
            </v-col>
          </v-row>
          <div v-else class="text-center grey--text text--darken-1 py-3">
            <v-icon small color="grey darken-1" class="mr-2">mdi-check-all</v-icon>
            Alle Mitarbeiter des Centers sind eingeplant oder werden angezeigt.
          </div>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="assignDialog" max-width="500" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Mitarbeiter zuweisen</v-card-title>
        <v-card-text class="pt-4">
          <div
            v-if="selectedUnassignedShift"
            class="mb-4 pa-3 rounded border-subtle"
            :class="isDarkMode ? 'grey darken-4 white--text' : 'grey lighten-4 black--text'">
            <strong>Schicht:</strong> {{ selectedUnassignedShift.shiftTemplate ? selectedUnassignedShift.shiftTemplate.name : "Custom" }}<br />
            <strong>Zeit:</strong> {{ formatShiftTime(selectedUnassignedShift) }}
          </div>
          <v-autocomplete
            v-model="selectedEmployeeId"
            :items="employees"
            item-text="fullName"
            item-value="_id"
            label="Mitarbeiter wählen"
            outlined
            dense
            :dark="isDarkMode"
            :light="!isDarkMode">
            <template v-slot:item="{ item }">
              <v-avatar size="24" class="mr-2"><img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" /></v-avatar>
              {{ item.fullName }}
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="assignDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveAssignment" :disabled="!selectedEmployeeId">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="swapDialog" max-width="450" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Schichten tauschen?</v-card-title>
        <v-card-text class="pt-2" :class="isDarkMode ? 'white--text' : 'black--text'" v-if="swapSource && swapTarget">
          <div class="d-flex align-center justify-space-between px-2 py-4 rounded mb-2" :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'">
            <div class="text-center">
              <div class="font-weight-bold text-h6">{{ swapSource.assignedEmployee.fullName }}</div>
              <div class="caption grey--text">von</div>
            </div>
            <v-icon color="blue accent-2">mdi-swap-horizontal</v-icon>
            <div class="text-center">
              <div class="font-weight-bold text-h6">{{ swapEmployee ? swapEmployee.fullName : "..." }}</div>
              <div class="caption grey--text">zu</div>
            </div>
          </div>
          <div class="text-center grey--text text--lighten-1">Datum: {{ $moment(swapSource.date).format("dddd, DD.MM.YYYY") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="swapDialog = false">Abbrechen</v-btn>
          <v-btn color="blue accent-2" depressed @click="executeSwap">Ja, Tauschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addShiftDialog" max-width="600" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title :class="isDarkMode ? 'white--text' : 'black--text'">Neue Schicht hinzufügen</v-card-title>
        <v-card-subtitle class="mt-1 grey--text text--lighten-1">für {{ $moment(newShiftData.date).format("dddd, DD.MM.YYYY") }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <div v-if="availableShifts.length === 0" class="text-center py-2">
            <v-progress-circular indeterminate small color="blue"></v-progress-circular> Lade Vorlagen...
          </div>

          <v-select
            :items="availableShifts"
            v-model="newShiftData.templateId"
            label="Vorlage laden (Optional)"
            item-text="name"
            item-value="_id"
            outlined
            dense
            dark
            clearable
            @change="onTemplateChange"
            class="mb-4">
            <template v-slot:item="{ item }"> <v-chip x-small :color="item.color" class="mr-2"></v-chip> {{ item.name }} </template>
          </v-select>

          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2 white--text">Zeiten & Pausen</span>
              <v-btn x-small color="blue" text @click="addTimeBlock"> <v-icon left x-small>mdi-plus</v-icon> Zeit </v-btn>
            </div>

            <div v-for="(time, i) in newShiftData.times" :key="i" class="d-flex align-center mb-2">
              <v-select
                v-model="time.type"
                :items="['Arbeitszeit', 'Pause']"
                dense
                outlined
                dark
                hide-details
                style="max-width: 130px"
                class="mr-2"></v-select>
              <v-text-field v-model="time.start" type="time" dense outlined dark hide-details class="mr-1 centered-input"></v-text-field>
              <span class="grey--text mx-2">-</span>
              <v-text-field v-model="time.end" type="time" dense outlined dark hide-details class="mr-2 centered-input"></v-text-field>
              <v-btn icon color="red lighten-2" small @click="removeTimeBlock(i)" :disabled="newShiftData.times.length === 1">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <v-textarea
            v-model="newShiftData.reason"
            label="Grund für manuelle Schicht (Optional)"
            outlined
            dense
            dark
            rows="2"
            placeholder="z.B. Krankheitsvertretung, Extra-Event..."></v-textarea>

          <v-autocomplete
            v-model="newShiftData.employeeId"
            :items="employees"
            item-text="fullName"
            item-value="_id"
            label="Mitarbeiter direkt zuweisen"
            outlined
            dense
            dark
            clearable>
            <template v-slot:item="{ item }">
              <v-avatar size="24" class="mr-2"><img :src="item.picture ? '/img/profilbilder' + item.picture : '/img/default-avatar.png'" /></v-avatar>
              {{ item.fullName }}
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="addShiftDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveNewShift" :disabled="newShiftData.times.length === 0">Erstellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "ShiftManagementRosterView",
  props: {
    availableEmployees: { type: Array, default: () => [] },
    availableStudios: { type: Array, default: () => [] },
    planData: { type: Array, required: true },
    employees: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    startDate: { type: String, required: true },
    schoolHolidays: { type: Array, default: () => [] },
    studioId: { type: String, default: null },
    availableShifts: { type: Array, default: () => [] },
    unassignedShifts: { type: Array, default: () => [] },
    externalShifts: { type: Array, default: () => [] },
    publicHolidays: { type: Array, default: () => [] },
    previousContext: { type: Object, default: null },
    isDarkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      openPanels: [0, 1, 2, 3, 4],
      showQualityPanel: false,
      selectedEmployeeToAdd: null,
      moveSource: null,
      isCompact: localStorage.getItem("rosterIsCompact") === "true",

      // Swap Dialog Data
      swapDialog: false,
      swapSource: null,
      swapTarget: null,
      swapEmployee: null, // Der Mitarbeiter, mit dem getauscht wird

      assignDialog: false,
      selectedUnassignedShift: null,
      selectedEmployeeId: null,

      addShiftDialog: false,
      newShiftData: {
        date: null,
        templateId: null,
        times: [{ start: "08:00", end: "17:00", type: "Arbeitszeit" }],
        reason: "",
        employeeId: null,
      },
    }
  },

  computed: {
    monthName() {
      return this.startDate ? this.$moment(this.startDate).format("MMMM YYYY") : ""
    },
    daysInMonth() {
      if (!this.startDate) return []
      const start = this.$moment(this.startDate).startOf("month")
      const end = this.$moment(this.startDate).endOf("month")
      const days = []
      let day = start.clone()
      while (day.isSameOrBefore(end, "day")) {
        days.push(day.clone())
        day.add(1, "day")
      }
      return days
    },
    colWidth() {
      return this.daysInMonth.length > 0 ? 100 / this.daysInMonth.length : 3
    },
    unassignedStats() {
      const totalCount = this.unassignedShifts.length
      return { totalCount }
    },
    showUnassignedRow() {
      return this.unassignedShifts.length > 0
    },

    // *** PLANUNGS-QUALITÄTS-SCORE ***
    qualityScore() {
      const emps = this.processedEmployees || []
      const totalShifts = (this.planData || []).length
      const unassigned = (this.unassignedShifts || []).length
      if (emps.length === 0 || totalShifts === 0) {
        return { score: 0, color: "grey", categories: [] }
      }

      const categories = []

      // === 2. STUNDEN-BALANCE (25 Punkte) ===
      const balanceIssues = []
      let balanceScore = 25
      const diffs = emps.map((e) => ({ name: e.fullName, diff: e.stats.diffMonth, soll: e.sollStunden || 0 }))
      const overWorked = diffs.filter((d) => d.diff > 8)
      const underWorked = diffs.filter((d) => d.diff < -8)

      overWorked.forEach((d) => {
        balanceScore -= Math.min(5, Math.ceil(Math.abs(d.diff) / 4))
        balanceIssues.push({ severity: d.diff > 16 ? "error" : "warning", text: `${d.name}: +${d.diff.toFixed(0)}h Überstunden` })
      })
      underWorked.forEach((d) => {
        balanceScore -= Math.min(5, Math.ceil(Math.abs(d.diff) / 4))
        balanceIssues.push({ severity: d.diff < -16 ? "error" : "warning", text: `${d.name}: ${d.diff.toFixed(0)}h Unterstunden` })
      })
      balanceScore = Math.max(0, balanceScore)
      categories.push({
        name: "Stunden-Balance",
        icon: "mdi-scale-balance",
        score: balanceScore,
        maxScore: 25,
        color: balanceScore >= 20 ? "green" : balanceScore >= 12 ? "orange" : "red",
        issues: balanceIssues,
      })

      // === 3. WOCHENEND-FAIRNESS (20 Punkte) ===
      const weIssues = []
      let weScore = 20
      const weCounts = emps.map((e) => ({ name: e.fullName, count: e.stats.weekendCount }))
      const maxWE = Math.max(...weCounts.map((w) => w.count), 0)
      const minWE = Math.min(...weCounts.map((w) => w.count), 0)
      const weSpread = maxWE - minWE

      if (weSpread > 2) {
        weScore -= Math.min(10, (weSpread - 2) * 3)
        const topWE = weCounts.filter((w) => w.count === maxWE)
        const bottomWE = weCounts.filter((w) => w.count === minWE)
        topWE.forEach((w) => weIssues.push({ severity: "warning", text: `${w.name}: ${w.count} WE (Maximum)` }))
        bottomWE.forEach((w) => weIssues.push({ severity: "info", text: `${w.name}: ${w.count} WE (Minimum)` }))
      }
      weCounts
        .filter((w) => w.count >= 4)
        .forEach((w) => {
          weScore -= 3
          weIssues.push({ severity: "error", text: `${w.name}: ${w.count} WE (sehr viel!)` })
        })
      weScore = Math.max(0, weScore)
      categories.push({
        name: "WE-Fairness",
        icon: "mdi-calendar-weekend",
        score: weScore,
        maxScore: 20,
        color: weScore >= 16 ? "green" : weScore >= 10 ? "orange" : "red",
        issues: weIssues,
      })

      // === 4. SCHICHTBLÖCKE (15 Punkte) ===
      const blockIssues = []
      let blockScore = 15
      let totalIslands = 0

      emps.forEach((e) => {
        const islands = Object.values(e.dailyStatus || {}).filter((s) => s && s.type === "shift" && s.data && s.data.isIsland)
        if (islands.length > 0) {
          totalIslands += islands.length
          blockIssues.push({
            severity: islands.length >= 3 ? "error" : "warning",
            text: `${e.fullName}: ${islands.length} Einzelschicht${islands.length > 1 ? "en" : ""}`,
          })
        }
      })
      blockScore -= Math.min(15, totalIslands * 3)
      blockScore = Math.max(0, blockScore)
      categories.push({
        name: "Blockbildung",
        icon: "mdi-view-sequential",
        score: blockScore,
        maxScore: 15,
        color: blockScore >= 12 ? "green" : blockScore >= 7 ? "orange" : "red",
        issues: blockIssues,
      })

      // === 5. RHYTHMUS-QUALITÄT (15 Punkte) ===
      const rhythmIssues = []
      let rhythmScore = 15
      let badTransitions = 0

      // Hilfsfunktion: Schichttyp basierend auf Startzeit
      const getShiftCategory = (times) => {
        if (!times || times.length === 0) return null
        const startStr = times[0].start || "08:00"
        const startHour = parseInt(startStr.split(":")[0], 10)
        const startMin = parseInt(startStr.split(":")[1] || "0", 10)
        const startTotal = startHour + startMin / 60
        if (startTotal < 11) return "morning"
        if (startTotal < 14) return "intermediate"
        return "late"
      }

      emps.forEach((e) => {
        const shifts = (e.shifts || []).sort((a, b) => a.date.localeCompare(b.date))
        for (let i = 1; i < shifts.length; i++) {
          const prev = shifts[i - 1]
          const curr = shifts[i]
          // Konsekutive Tage?
          const prevDate = this.$moment(prev.date)
          const currDate = this.$moment(curr.date)
          if (currDate.diff(prevDate, "days") !== 1) continue

          const prevTimes = prev.times || (prev.shiftTemplate ? prev.shiftTemplate.times : [])
          const currTimes = curr.times || (curr.shiftTemplate ? curr.shiftTemplate.times : [])

          const prevType = getShiftCategory(prevTimes)
          const currType = getShiftCategory(currTimes)

          // Gleicher Schichttyp = kein Problem (auch bei 30min Abweichung)
          if (!prevType || !currType || prevType === currType) continue

          // Nur echte Spät→Früh Wechsel sind problematisch
          if (prevType === "late" && currType === "morning") {
            badTransitions++
            rhythmIssues.push({ severity: "warning", text: `${e.fullName}: Spät→Früh am ${this.$moment(curr.date).format("DD.MM")}` })
          }
          // Intermediate→Morning ist weniger schlimm, aber erwähnenswert bei häufigem Vorkommen
          else if (prevType === "late" && currType === "intermediate") {
            // Nur als Info, kein Punktabzug
            rhythmIssues.push({ severity: "info", text: `${e.fullName}: Spät→Zwischen am ${this.$moment(curr.date).format("DD.MM")}` })
          }
        }
      })
      rhythmScore -= Math.min(15, badTransitions * 4)
      rhythmScore = Math.max(0, rhythmScore)
      categories.push({
        name: "Rhythmus",
        icon: "mdi-sleep",
        score: rhythmScore,
        maxScore: 15,
        color: rhythmScore >= 12 ? "green" : rhythmScore >= 7 ? "orange" : "red",
        issues: rhythmIssues,
      })

      // === GESAMT ===
      const totalScore = categories.reduce((sum, c) => sum + c.score, 0)
      const totalMax = categories.reduce((sum, c) => sum + c.maxScore, 0)
      const percentage = Math.round((totalScore / totalMax) * 100)

      let color = "green"
      if (percentage < 70) color = "red"
      else if (percentage < 85) color = "orange"

      return { score: percentage, color, categories }
    },
    processedEmployees() {
      if (!this.employees || this.employees.length === 0) return []
      const currentPlan = this.planData || []

      return this.employees
        .map((emp) => {
          const empId = String(emp._id)
          const empUserId = emp.user._id || emp.user

          const shifts = currentPlan
            .filter((plan) => {
              if (!plan.assignedEmployee) return false
              const planAssignedId = String(plan.assignedEmployee._id || plan.assignedEmployee)
              return planAssignedId === String(empId) || planAssignedId === String(empUserId)
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date))

          const dailyStatus = {}
          const workedWeekends = new Set()
          let types = { morning: 0, intermediate: 0, late: 0 }

          shifts.forEach((shift, index) => {
            const times = shift.times || (shift.shiftTemplate ? shift.shiftTemplate.times : [])
            if (times.length > 0) {
              const end = times[times.length - 1].end
              const endHour = parseInt(end.split(":")[0], 10)
              if (endHour <= 18) types.morning++
              else if (endHour >= 20) types.late++
              else types.intermediate++
            }

            const currentDay = this.$moment(shift.date)
            let connectedYesterday = false
            const prevShift = shifts[index - 1]
            if (prevShift && this.$moment(prevShift.date).isSame(currentDay.clone().subtract(1, "days"), "day")) connectedYesterday = true
            let connectedTomorrow = false
            const nextShift = shifts[index + 1]
            if (nextShift && this.$moment(nextShift.date).isSame(currentDay.clone().add(1, "days"), "day")) connectedTomorrow = true

            if (emp.isApprentice) {
              const yDay = currentDay.clone().subtract(1, "days").isoWeekday()
              const tDay = currentDay.clone().add(1, "days").isoWeekday()
              if (emp.schoolDays && emp.schoolDays.some((s) => s.dayOfWeek === yDay)) connectedYesterday = true
              if (emp.schoolDays && emp.schoolDays.some((s) => s.dayOfWeek === tDay)) connectedTomorrow = true
            }

            // NEU: Vormonats-Kontext berücksichtigen – wenn MA am Ende des Vormonats gearbeitet hat
            if (!connectedYesterday && this.previousContext) {
              const prevCtx = this.getEmployeePrevContext({ _id: empId })
              if (prevCtx && prevCtx.streak > 0) {
                // Check: Ist der aktuelle Tag am Monatsanfang (1. oder 2.)?
                const dayOfMonth = currentDay.date()
                if (dayOfMonth <= prevCtx.streak) {
                  connectedYesterday = true // Streak vom Vormonat zählt als Verbindung
                }
              }
            }

            if (!connectedYesterday && !connectedTomorrow) {
              shift.isIsland = true
              shift.warning = "Einzel-Schicht"
            }
          })

          this.daysInMonth.forEach((day) => {
            const dateStr = day.format("YYYY-MM-DD")
            const dayOfWeekISO = day.isoWeekday()
            let status = null

            const shiftsOnDay = shifts.filter((s) => s.date === dateStr)
            if (shiftsOnDay.length > 0) {
              if (shiftsOnDay.some((s) => s.isVirtualExternal)) {
                status = { type: "externalShift", data: shiftsOnDay[0], allShifts: shiftsOnDay }
              } else {
                status = { type: "shift", data: shiftsOnDay[0], allShifts: shiftsOnDay }
                if (dayOfWeekISO >= 6) workedWeekends.add(day.isoWeek())
              }
            }

            if (!status) {
              const extShift = this.externalShifts.find((s) => {
                const sEmpId = String(s.assignedEmployee?._id || s.assignedEmployee)
                return (sEmpId === String(empId) || sEmpId === String(empUserId)) && s.date === dateStr
              })
              if (extShift) {
                status = { type: "externalShift", data: extShift }
              }
            }

            if (!status) {
              const approvedAbsence = (emp.sickEntries || []).find((abs) => abs.sickDays && abs.sickDays.some((sd) => sd.date === dateStr))
              if (approvedAbsence) {
                status = {
                  type: approvedAbsence.absenceReason || "sick",
                  isPending: approvedAbsence.status !== "accepted",
                  note: approvedAbsence.note,
                }
              } else if (
                emp.vacations &&
                emp.vacations.find(
                  (vac) => vac.dates && vac.dates.length > 0 && day.isBetween(vac.dates[0].date, vac.dates[vac.dates.length - 1].date, null, "[]"),
                )
              ) {
                status = { type: "vacation" }
              } else if ((emp.freeWishes || []).find((w) => this.$moment(w.day).format("YYYY-MM-DD") === dateStr)) {
                status = { type: "freeWish" }
              } else {
                const isSchoolHoliday = (this.schoolHolidays || []).some(
                  (sh) => sh.startDate && sh.endDate && day.isBetween(sh.startDate, sh.endDate, null, "[]"),
                )
                if (emp.isApprentice && !isSchoolHoliday && emp.schoolDays && emp.schoolDays.some((sd) => sd.dayOfWeek === dayOfWeekISO)) {
                  status = { type: "school" }
                } else if (emp.planningSettings?.recurringAbsence?.includes(dayOfWeekISO)) {
                  status = { type: "recurringAbsence" }
                } else if (emp.planningSettings?.recurringExternalStudio?.includes(dayOfWeekISO)) {
                  status = { type: "recurringExternalStudio" }
                }
              }
            }
            dailyStatus[dateStr] = status
          })

          const planned = emp.plannedHours || 0
          const soll = emp.sollStunden || 0
          const diffMonth = planned - soll

          let statusText = "OK"
          if (diffMonth > 12) statusText = "Überstunden Warnung"
          if (diffMonth < -12) statusText = "Unterstunden Warnung"

          return {
            ...emp,
            studioTitle: emp.studio && emp.studio.length > 0 ? emp.studio[0].title : "",
            shifts,
            dailyStatus,
            stats: { weekendCount: workedWeekends.size, diffMonth, types, statusText },
          }
        })
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
  },
  methods: {
    toggleCompact() {
      this.isCompact = !this.isCompact
      localStorage.setItem("rosterIsCompact", String(this.isCompact))
    },
    isAdditionalStudioShift(shift) {
      if (!shift || !shift.studio) return false
      const sId = typeof shift.studio === "object" ? shift.studio._id : shift.studio
      return String(sId) !== String(this.studioId)
    },
    getStudioAbbreviation(studio) {
      const name = this.getStudioName(studio)
      return name ? name.substring(0, 2).toUpperCase() : ""
    },
    getStudioName(studioIdOrObj) {
      if (!studioIdOrObj) return ""
      if (typeof studioIdOrObj === "object" && studioIdOrObj.name) return studioIdOrObj.name
      if (typeof studioIdOrObj === "object" && studioIdOrObj.title) return studioIdOrObj.title
      const id = typeof studioIdOrObj === "object" ? studioIdOrObj._id : studioIdOrObj
      const found = this.availableStudios.find((s) => String(s._id) === String(id))
      return found ? found.name : ""
    },
    emitAddEmployee() {
      if (this.selectedEmployeeToAdd) {
        this.$emit("add-employee", this.selectedEmployeeToAdd)
        this.selectedEmployeeToAdd = null // Dropdown zurücksetzen
      }
    },
    startMove(shift) {
      this.moveSource = shift
      this.$toast.info("Verschieben aktiv: Klicke auf ein Ziel im gleichen Tag.")
    },
    cancelMove() {
      this.moveSource = null
    },

    async handleCellClick(employee, dateStr) {
      if (!this.moveSource) return

      if (this.moveSource.date !== dateStr) {
        this.$toast.warning("Verschieben nur am gleichen Tag möglich.")
        return
      }

      const targetShift = employee.dailyStatus[dateStr] && employee.dailyStatus[dateStr].type === "shift" ? employee.dailyStatus[dateStr].data : null

      if (targetShift && targetShift._id === this.moveSource._id) {
        this.cancelMove()
        return
      }

      const employeeUserId = employee._id

      try {
        if (targetShift) {
          // SWAP: Öffne Dialog statt sofort loszulegen
          this.swapSource = this.moveSource
          this.swapTarget = targetShift
          this.swapEmployee = employee
          this.swapDialog = true
          // MoveSource nicht resetten, damit wir bei Cancel noch im Modus sind?
          // Nein, besser wir bleiben im Modus bis executed.
        } else {
          // MOVE (leer) - direkt machen
          await api.shiftManagement.updateShift(this.moveSource._id, {
            assignedEmployee: employeeUserId,
            allocationNote: `Verschoben zu ${employee.fullName} (Manuell)`,
          })
          this.$toast.success("Schicht verschoben!")
          this.cancelMove()
          this.$emit("refresh")
        }
      } catch (e) {
        this.$toast.error("Fehler beim Verschieben")
        console.error(e)
        this.cancelMove()
      }
    },

    async executeSwap() {
      if (!this.swapSource || !this.swapTarget || !this.swapEmployee) return

      try {
        const employeeUserId = this.swapEmployee._id

        // A -> B
        await api.shiftManagement.updateShift(this.swapSource._id, {
          assignedEmployee: employeeUserId,
          allocationNote: `Getaucht mit ${this.swapTarget.assignedEmployee.fullName} (Manuell)`,
        })

        // B -> A
        const sourceEmpId = this.swapSource.assignedEmployee ? this.swapSource.assignedEmployee._id || this.swapSource.assignedEmployee : null
        await api.shiftManagement.updateShift(this.swapTarget._id, {
          assignedEmployee: sourceEmpId,
          status: sourceEmpId ? "assigned" : "unassigned",
          allocationNote: `Getaucht mit ${this.swapEmployee.fullName} (Manuell)`,
        })

        this.$toast.success("Erfolgreich getauscht")
        this.$emit("refresh")
      } catch (e) {
        console.error(e)
        this.$toast.error("Tausch fehlgeschlagen")
      } finally {
        this.swapDialog = false
        this.cancelMove()
      }
    },

    async deleteShift(shift) {
      if (confirm("Schicht wirklich löschen?")) {
        try {
          await api.shiftManagement.deleteShift(shift._id)
          this.$toast.success("Gelöscht")
          this.$emit("refresh")
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
      }
    },

    // --- UNASSIGNED LOGIC ---
    openAssignDialog(shift) {
      this.selectedUnassignedShift = shift
      this.selectedEmployeeId = null
      this.assignDialog = true
    },
    async saveAssignment() {
      if (!this.selectedEmployeeId || !this.selectedUnassignedShift) return
      try {
        await api.shiftManagement.updateShift(this.selectedUnassignedShift._id, {
          assignedEmployee: this.selectedEmployeeId,
          status: "assigned",
          allocationNote: "Manuell zugewiesen (Rest)",
        })
        this.$toast.success("Zugewiesen")
        this.assignDialog = false
        this.$emit("refresh")
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },

    // --- ADD SHIFT LOGIC (UPDATED) ---
    // Jetzt mit Parametern für Pre-Fill
    openAddShiftDialog(arg1, arg2) {
      let dateVal = ""
      let empId = null
      let empStudioId = this.studioId

      // Wenn Arg1 ein Employee Object ist (Klick im Grid)
      if (arg1 && arg1.fullName) {
        empId = arg1._id
        dateVal = arg2 // Datum kommt als 2. Arg
        empStudioId = arg1.loadedFromStudioId || this.studioId
      } else {
        // Wenn Arg1 ein Moment-Objekt ist (Klick im Header)
        dateVal = arg1.format("YYYY-MM-DD")
      }

      this.newShiftData = {
        date: dateVal,
        templateId: null,
        times: [{ start: "08:00", end: "17:00", type: "Arbeitszeit" }],
        reason: "",
        employeeId: empId, // Pre-Set
        studioId: empStudioId,
      }
      this.addShiftDialog = true
    },
    onTemplateChange(val) {
      const tpl = this.availableShifts.find((s) => s._id === val)
      if (tpl && tpl.times && tpl.times.length) {
        this.newShiftData.times = JSON.parse(JSON.stringify(tpl.times))
      }
    },
    addTimeBlock() {
      const last = this.newShiftData.times[this.newShiftData.times.length - 1]
      const type = last.type === "Arbeitszeit" ? "Pause" : "Arbeitszeit"
      this.newShiftData.times.push({ start: last.end, end: this.$moment(last.end, "HH:mm").add(1, "hour").format("HH:mm"), type })
    },
    removeTimeBlock(index) {
      this.newShiftData.times.splice(index, 1)
    },
    async saveNewShift() {
      try {
        const payload = {
          studio: this.newShiftData.studioId || this.studioId,
          date: this.newShiftData.date,
          times: this.newShiftData.times,
          shiftTemplate: this.newShiftData.templateId,
          assignedEmployee: this.newShiftData.employeeId,
          allocationNote: this.newShiftData.reason ? `Manuell: ${this.newShiftData.reason}` : "Manuell zugewiesen",
          status: this.newShiftData.employeeId ? "assigned" : "unassigned",
        }
        await api.shiftManagement.createManualShift(payload)
        this.$toast.success("Schicht erstellt")
        this.addShiftDialog = false
        this.$emit("refresh")
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Erstellen")
      }
    },

    // --- HELPERS ---
    handleShiftClick(shift) {},
    isSpecialShift(shift) {
      if (!shift.shiftTemplate) return true
      if (shift.isSpecialDemand === true) return true
      if (shift.allocationNote && (shift.allocationNote.includes("Spezial") || shift.allocationNote.includes("Fix"))) return true
      return false
    },
    getUnassignedShiftsForDay(momentDate) {
      const dateStr = momentDate.format("YYYY-MM-DD")
      return this.unassignedShifts.filter((s) => s.date === dateStr)
    },
    formatShiftTime(shift) {
      if (!shift.times || shift.times.length === 0) {
        if (shift.shiftTemplate && shift.shiftTemplate.times) {
          const first = shift.shiftTemplate.times[0]
          const last = shift.shiftTemplate.times[shift.shiftTemplate.times.length - 1]
          return `${first.start} - ${last.end}`
        }
        return "?"
      }
      const first = shift.times[0]
      const last = shift.times[shift.times.length - 1]
      return `${first.start} - ${last.end}`
    },
    getStatusColor(type) {
      const map = {
        vacation: "blue lighten-1",
        freeWish: "pink lighten-2",
        school: "purple lighten-2",
        recurringAbsence: "grey darken-3",
        publicHoliday: "amber darken-2",
        recurringExternalStudio: "cyan darken-2",
        sick: "red lighten-1",
        military: "deep-purple lighten-1",
        pregnancy: "teal lighten-1",
        other: "grey lighten-1",
      }
      return map[type] || "grey"
    },
    getStatusIcon(type) {
      const map = {
        vacation: "mdi-palm-tree",
        freeWish: "mdi-heart",
        school: "mdi-school",
        recurringAbsence: "mdi-close",
        publicHoliday: "mdi-star-four-points",
        recurringExternalStudio: "mdi-office-building",
        sick: "mdi-emoticon-sad",
        military: "mdi-shield",
        pregnancy: "mdi-baby-carriage",
        other: "mdi-alert-circle-outline",
      }
      return map[type] || "mdi-help"
    },
    getStatusText(type) {
      const map = {
        vacation: "Ferien",
        freeWish: "Wunschfrei",
        school: "Berufsschule",
        recurringAbsence: "Sperrtag",
        publicHoliday: "Feiertag",
        recurringExternalStudio: "Anderes Studio",
        sick: "Krank",
        military: "Militär",
        pregnancy: "Schwangerschaft",
        other: "Anderes",
      }
      return map[type] || ""
    },

    // *** NEU: FEIERTAG HELPERS ***
    isPublicHoliday(momentDay) {
      if (!this.publicHolidays || this.publicHolidays.length === 0) return false
      const dateStr = momentDay.format("YYYY-MM-DD")
      return this.publicHolidays.some((h) => h.date === dateStr)
    },
    isPublicHolidayByStr(dateStr) {
      if (!this.publicHolidays || this.publicHolidays.length === 0) return false
      return this.publicHolidays.some((h) => h.date === dateStr)
    },
    getPublicHolidayName(momentDay) {
      if (!this.publicHolidays) return ""
      const dateStr = momentDay.format("YYYY-MM-DD")
      const holiday = this.publicHolidays.find((h) => h.date === dateStr)
      return holiday ? holiday.name : ""
    },

    // *** NEU: VORMONATS-KONTEXT HELPERS ***
    getEmployeePrevContext(employee) {
      if (!this.previousContext) return null
      const empId = String(employee._id)
      const streak = this.previousContext.streakMap ? this.previousContext.streakMap[empId] || 0 : 0
      const weStats = this.previousContext.weekendStats ? this.previousContext.weekendStats[empId] : null
      const prevWeekends = weStats ? weStats.count : 0
      if (streak === 0 && prevWeekends === 0) return null
      return { streak, prevWeekends }
    },
  },
}
</script>

<style scoped>
.compact-mode-panels >>> .v-expansion-panel-header {
  min-height: 40px !important;
  height: 40px !important;
}
/* GENERAL LAYOUT */
.employee-col-header,
.employee-col-info {
  width: 280px;
  flex-shrink: 0;
  min-width: 280px;
}
.calendar-grid {
  overflow: hidden;
}
.day-col {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
}
.weekend-header {
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffcc80;
  white-space: nowrap !important;
}
.weekend-bg {
  background-color: rgba(255, 255, 255, 0.03);
}
.holiday-header {
  background-color: rgba(255, 193, 7, 0.15) !important;
  border-bottom: 2px solid rgba(255, 193, 7, 0.4);
}
.holiday-bg {
  background-color: rgba(255, 193, 7, 0.06) !important;
}
.lh-1 {
  line-height: 1.1;
}
.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}
.border-left-light {
  border-left: 1px solid rgba(255, 255, 255, 0.03);
}
.border-top-dashed {
  border-top: 1px dashed rgba(255, 255, 255, 0.2);
}
.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
}
.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* SHIFT PILL & INTERACTION */
.shift-pill {
  width: 90%;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: all 0.2s;
}
.shift-pill:hover {
  transform: scale(1.05);
  z-index: 2;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.5);
}
.shift-pill-special {
  border: 2px solid #ffd700 !important;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
}
.shift-pill-island {
  border: 2px dashed #ff5252 !important;
}
.shift-moving {
  animation: pulse 1s infinite;
  border: 2px solid #448aff !important;
  z-index: 10;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(68, 138, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(68, 138, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(68, 138, 255, 0);
  }
}

@keyframes pulse-opacity {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}
.pulse-slow {
  animation: pulse-opacity 2s infinite;
}

.drop-zone {
  transition: background-color 0.2s;
  cursor: pointer;
}

/* CONTAINER STATE (PERFORMANCE FIX) */
.moving-mode .day-col {
  opacity: 0.3;
  filter: grayscale(100%);
  pointer-events: none; /* Block interactions on invalid targets */
  transition: opacity 0.2s;
}

/* EXCEPTION: ACTIVE COLUMN */
.moving-mode .day-col.drop-active {
  opacity: 1;
  filter: none;
  pointer-events: auto; /* Allow interaction */
  background-color: rgba(68, 138, 255, 0.15) !important;
  box-shadow: inset 0 0 0 2px #448aff;
  z-index: 5;
}

/* ADD BUTTON ON HOVER (GRID CELLS) */
.hover-add-btn {
  opacity: 0;
  transition: opacity 0.2s;
}
.cell-hover-trigger:hover .hover-add-btn {
  opacity: 1;
}

/* DOTS & BUTTONS */
.unassigned-dot {
  background: #ff9800;
  color: #000;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.pulse-hover:hover {
  animation: pulse 1s infinite;
}

/* ADD BTN HEADER (Legacy) */
.add-shift-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  opacity: 0;
  transition: opacity 0.2s;
}
.day-col:hover .add-shift-btn {
  opacity: 1;
}

.legend-box {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #1e1e1e;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #616161;
}

.centered-input >>> input {
  text-align: center;
}

/* Roster Root & Bar */
.roster-root {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
}
.roster-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.roster-bar-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
}
.roster-bar-stats {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 4px;
}
.roster-bar-dot {
  color: rgba(255, 255, 255, 0.15);
}
.roster-legend {
  display: flex;
  align-items: center;
  gap: 10px;
}
.roster-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.35);
}
.roster-ldot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.roster-quality-btn {
  border-radius: 6px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 0.7rem !important;
  letter-spacing: 0.3px !important;
}

/* LIGHT MODE STYLES OVERRIDES */
.roster-root.light-mode {
  background: #f5f5f7;
}
.roster-root.light-mode .roster-bar {
  background: rgba(255, 255, 255, 0.8) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .roster-bar-title {
  color: #1d1d1f !important;
}
.roster-root.light-mode .roster-bar-stats {
  color: rgba(0, 0, 0, 0.45) !important;
}
.roster-root.light-mode .roster-legend-item {
  color: rgba(0, 0, 0, 0.45) !important;
}
.roster-root.light-mode .border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.roster-root.light-mode .day-col {
  border-right: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.roster-root.light-mode .weekend-header {
  background-color: rgba(0, 0, 0, 0.03) !important;
  color: #fb8c00 !important;
}
.roster-root.light-mode .weekend-bg {
  background-color: rgba(0, 0, 0, 0.01) !important;
}
.roster-root.light-mode .holiday-header {
  background-color: rgba(255, 193, 7, 0.1) !important;
  border-bottom: 2px solid rgba(255, 193, 7, 0.3) !important;
}
.roster-root.light-mode .holiday-bg {
  background-color: rgba(255, 193, 7, 0.04) !important;
}
.roster-root.light-mode .cell-hover-trigger {
  color: #1d1d1f !important;
}
.roster-root.light-mode .white--text {
  color: #1d1d1f !important;
}
.roster-root.light-mode .grey--text {
  color: rgba(0, 0, 0, 0.5) !important;
}
.roster-root.light-mode .grey.darken-4,
.roster-root.light-mode .grey.darken-3,
.roster-root.light-mode .grey.darken-2 {
  background-color: #ffffff !important;
  color: #1d1d1f !important;
}
.roster-root.light-mode .v-list {
  background-color: #ffffff !important;
}
.roster-root.light-mode .v-card {
  background-color: #ffffff !important;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-track {
  background: #f5f5f7;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #d1d1d6;
}
.roster-root.light-mode .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #acacad;
}
</style>
